/**
 * Child PO marketplace repost — shared front-end constants.
 * Mirror semantics: backend/src/config/childPOMarketplaceRepost.ts + tranzitMultiLoadService.
 */
import { isPoAwaitingCancellationReassignChannel } from './cancellationReassignment';
import { PARTNER_REQUEST_STATUS } from './notificationTypes';
import { NOTIFICATION_TYPES } from './notificationTypes';

export const CHILD_PO_CARRIER_OPTION_MARKETPLACE = 'marketplace' as const;

/** PO form / schedule value for partnered (non-marketplace) child loads. */
export const CHILD_PO_CARRIER_OPTION_PARTNER = 'carrier' as const;

/**
 * Whether a PO doc is an open marketplace listing for carrier browse.
 * Explicit partner carrierOption always wins over a stale isMarketplace flag.
 */
export function isPoDocOpenMarketplaceListing(
  poData: Record<string, unknown> | null | undefined
): boolean {
  if (!poData) return false;
  if (isPoAwaitingCancellationReassignChannel(poData)) return false;
  if (poData.carrierOption === CHILD_PO_CARRIER_OPTION_PARTNER) return false;
  const noAssignedCarrier =
    !poData.carrierId || poData.carrierId === null || poData.carrierId === '';
  if (!noAssignedCarrier) return false;
  return (
    poData.carrierOption === CHILD_PO_CARRIER_OPTION_MARKETPLACE ||
    poData.isMarketplace === true
  );
}

/**
 * Carrier-visible marketplace load row must be bookable (not incomplete/junk leftovers).
 * Applied after PO open-listing checks so cancelled POs never reach this.
 */
export function isBookableMarketplaceLoadListing(load: {
  rate?: number | string | null;
  isMarketplace?: boolean;
  carrierId?: string | null;
  pickupLocation?: { address?: string; city?: string } | null;
  deliveryLocation?: { address?: string; city?: string } | null;
  pickup?: { address?: string } | null;
  delivery?: { address?: string } | null;
}): boolean {
  if (load.isMarketplace !== true) return false;
  if (load.carrierId != null && load.carrierId !== '') return false;
  const rate = typeof load.rate === 'number' ? load.rate : Number(load.rate);
  if (!Number.isFinite(rate) || rate <= 0) return false;
  const pickup = String(
    load.pickupLocation?.address || load.pickupLocation?.city || load.pickup?.address || ''
  ).trim();
  const delivery = String(
    load.deliveryLocation?.address ||
      load.deliveryLocation?.city ||
      load.delivery?.address ||
      ''
  ).trim();
  if (pickup.length < 2 || delivery.length < 2) return false;
  return true;
}

/** Partner request statuses closed when a child PO is reposted to marketplace. */
export const CHILD_PO_PARTNER_ARTIFACT_CLOSURE_STATUSES = [
  PARTNER_REQUEST_STATUS.PENDING,
  PARTNER_REQUEST_STATUS.OFFERED,
  PARTNER_REQUEST_STATUS.ACCEPTED,
] as const;

export const CHILD_PO_MARKETPLACE_REPOST_CANCELLATION_REASON =
  'Child PO reposted to marketplace' as const;

/** Notification types created for partnered (non-marketplace) child PO assignment. */
export const CHILD_PO_PARTNER_ASSIGNMENT_NOTIFICATION_TYPES = [
  NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
  NOTIFICATION_TYPES.PARTNER_REQUEST,
  NOTIFICATION_TYPES.MAKE_OFFER,
  NOTIFICATION_TYPES.LOAD_REQUEST,
] as const;

/** Notification types cancelled alongside partner requests on marketplace repost. */
export const CHILD_PO_MARKETPLACE_REPOST_NOTIFICATION_TYPES = [
  NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
  NOTIFICATION_TYPES.MAKE_OFFER,
  NOTIFICATION_TYPES.MARKETPLACE_APPROVAL_RATE_CONFIRMATION,
  'load_request',
] as const;

/**
 * Stale carrier offer_rejected notifications superseded when a child PO is reposted.
 * Informational only — marketplace browse does not hide loads based on offer_rejected.
 */
export const CHILD_PO_MARKETPLACE_REPOST_BROWSE_SUPPRESSION_TYPES = [
  NOTIFICATION_TYPES.OFFER_REJECTED,
] as const;
